/**
 * The account book's WRITE calls (T651 M3) — create, edit, deactivate.
 *
 * ⛔ WHY THIS IS ITS OWN MODULE AND NOT AN APPEND TO lib/actions.ts. That file
 * froze at T651 M2 so that three milestones filling three sibling screens could
 * run in parallel without editing the same declarations; the stated hazard is
 * "two adjacent appends to a shared client file". A per-area module produces
 * ZERO appends to any shared client file, which removes the hazard rather than
 * managing it. The reads still live in actions.ts because they were landed by
 * the milestone that froze it - a write action posting to a route nobody had
 * written yet would have been dead code.
 * ⚠️ Recorded honestly as a DEPARTURE from the shipped house convention:
 * screens/admin/Operators.tsx imports its write actions from actions.ts.
 *
 * ⛔ THE COPY IS ALWAYS THIS FILE'S OWN, NEVER THE SERVER'S SENTENCE (SEC-5),
 * matching actions.ts's adminFailure. The route wording is terse and
 * developer-shaped; these are the ones an operator reads.
 *
 * ⚠️ T699 NARROWED THAT SENTENCE AND IT IS RESTATED HERE RATHER THAN LEFT TO
 * READ AS FALSE. It used to say the copy comes from the STATUS and never from the
 * body, and that "there is exactly one body-rendering exception in this client
 * and it is not here". The second half is STILL TRUE — actions.ts's 403 arm
 * remains the only place server prose reaches a screen, and this file is still
 * not it. What changed is the first half: since T699 the 400 arm reads a
 * machine-readable `code` OFF THE BODY, purely to CHOOSE between sentences
 * written here. Reading the body to KEY a copy map is what client/lib/api.ts
 * documents `ApiError.body` for; RENDERING it is the exception. See
 * shared/registry-error-schema.ts for why that distinction is the whole design.
 */
import { ApiError, getJson, postJson } from './api.js';
import { isRegistryErrorCode, type RegistryErrorCode } from '../../shared/registry-error-schema.js';

/**
 * The client's OWN sentence per refusal code (T699), the sibling of the map in
 * destination-actions.ts. ⛔ NOT the server's wording — SEC-5 keeps that off
 * screen; the CODE is what crosses the wire.
 *
 * ⚠️ WORDED FOR SIX FIELDS, WHICH IS WHY IT DOES NOT MATCH ITS DESTINATION
 * SIBLING WORD FOR WORD. One reader validates the account name plus five
 * optional address lines, and the code names the RULE, not the field (a
 * recorded T699 decision — six fields would mean a code set six times the size
 * on a form whose hard constraint is simplicity). So this copy says "one of the
 * details" where the destination form, having a single field, can say "that
 * name". Do not "harmonise" the two: the imprecision here is honest, and the
 * precision there is earned.
 */
const REGISTRY_ERROR_COPY: Readonly<Record<RegistryErrorCode, string>> = {
  field_too_long: 'One of the details you typed is too long to save. Shorten it and try again.',
  // ⛔ DOES NOT SAY "INVISIBLE" — U+00B7 MIDDLE DOT is in the refused set and is
  // visible, as are the compatibility ligatures a word processor produces.
  field_charset:
    'One of the details you typed has a character that cannot be saved. It usually comes from '
    + 'copying and pasting. Type it in by hand and try again.',
  /**
   * T1719 M3. Both of these are 409s, and both used to be REPLACED by the
   * duplicate-name sentence below, because the 409 arm keyed on the STATUS
   * alone. That sent Helen to change a name the list had just proved unique.
   * ⛔ THE SHORT CODE IS NAMED, because she cannot act on a refusal that does
   * not say which box is wrong.
   */
  /**
   * ⛔⛔ AND IT SAYS **NOTHING WAS SAVED**, for the same reason its locked
   * sibling below does, and it did not until T1719 M7 (Captain's M6 gate, item
   * 6a). This is a POST-WRITE 23505 on the update path, which has NO pre-check
   * by design, so the refusal rolls the whole transaction back: a town or a
   * phone number the operator corrected in the same save is gone too. It was the
   * one member of `SHORT_CODE_REFUSALS` whose sentence did not say so, and a
   * refusal that names only the code invites her to assume the rest landed and
   * move on. The wire's own copy (`app.ts`, `short_code_conflict`) says the same
   * thing in its own words; both were changed together, because Helen reads this
   * one and only Helen reads it.
   */
  short_code_conflict:
    'Another account already uses that short code. Nothing was saved. Give this one different '
    + 'letters.',
  /**
   * ⛔ THE REMEDY IS NOT RETYPING, so the sentence must not invite it: once a
   * job reference has gone out under a code, changing the code would leave a
   * home holding a reference this system no longer recognises (DD4). Asking
   * Alex is the recorded escape hatch, so the copy says exactly that.
   *
   * ⛔⛔ AND IT SAYS **NOTHING WAS SAVED**, BECAUSE NOTHING WAS. An earlier
   * wording promised "Everything else on the form still saves" and that was
   * FALSE (T1719 M3 output gate, D3): `updateAccountOn` throws
   * `AccountShortCodeLockedError` BEFORE its UPDATE, inside the caller's single
   * `withTransaction` BEGIN/COMMIT, so the whole edit is refused and the
   * transaction writes nothing. Measured on real Postgres: a town correction
   * posted alongside a refused code change is ROLLED BACK. A refusal that tells
   * the operator her other edits landed is worse than a bare failure - she
   * closes the form and the correction is gone.
   * ⇒ the sentence states the true contract AND the two ways forward, because
   * "nothing was saved" without a next step just moves the confusion.
   */
  short_code_locked:
    'That account\'s code is already on a job reference issued or set aside for the homes\' '
    + 'portal, so the code cannot change. '
    + 'Nothing was saved. Put the code back the way it was to save the rest, or ask Alex if it '
    + 'really has to change.',
  /**
   * T1719 M4. ⛔ THE ONLY SENTENCE IN THIS MAP THAT MUST NOT INVITE AN EDIT.
   * Postgres refused to wait for another transaction (55P03, three seconds).
   * Nothing was typed wrong and nothing was saved, so the instruction is to
   * change NOTHING and press save again.
   *
   * ⛔⛔ IT NAMES NO RESOURCE, AND TWO EARLIER WORDINGS DID — EACH MEASURABLY
   * FALSE. The server's predicate keys on the SQLSTATE alone and `lock_timeout`
   * bounds EVERY lock wait on the pool, so the only honest claim is "something
   * was held":
   *   · "That account is busy - someone else is saving a change to it" was false
   *     when the GLOBAL account write lock was the thing held (measured, 3.06 s),
   *     so a quiet account reported itself busy;
   *   · "Another ACCOUNT change is being saved" was false when a BOOKINGS ROW
   *     was the thing held (measured at M4c, 3010 ms), so two operators on the
   *     same run were told an account was being edited.
   * ⇒ resource-neutral. Naming a resource again needs a NARROWER server
   * predicate first, not a braver sentence here.
   * ⚠️ IT ALSO DOES NOT SAY "BUSY SERVER" or name a lock: the true, actionable
   * thing is that another account change is in flight and this one can simply be
   * pressed again.
   * ⚠️ ASCII HERE IS GRADED BY THIS FILE'S OWN SPEC, NOT BY `check:copy`
   * (corrected at M4d): that script's kiosk-client block bans the EM DASH only,
   * so a curly apostrophe or an en dash would pass it. The real guard is
   * `test/client/account-actions.spec.ts`'s `/^[\x20-\x7E]+$/`.
   */
  account_busy:
    'Another change is being saved right now. Nothing was saved. Wait a moment and '
    + 'press save again.',
  /**
   * ⭐⭐ T1719 M6 — THE THREE SENTENCES M5 OWED AND DID NOT WRITE (Captain's M5
   * gate, item 1). M5 put the short-code box on the Add and Edit forms; the
   * server's three shape/denylist refusals are 400s that carried NO code, so
   * every one of them arrived here through the codeless 400 fallback below -
   * "Check the details. An account needs a name, and each line has to be short
   * enough to fit on a booking." Helen, looking at the short-code box she just
   * typed `WARD` into, was told to check the name.
   *
   * ⛔ ALL THREE NAME THE SHORT CODE AND END WITH "NOTHING WAS SAVED", for the
   * same reason `short_code_locked` does: `readAccountWrite` refuses BEFORE the
   * transaction opens, so the town correction she typed in the same form is gone
   * too. A refusal that lets her believe the rest landed is worse than a bare
   * failure.
   * ⚠️ ASCII ONLY, graded by `test/client/account-actions.spec.ts`'s
   * `/^[\x20-\x7E]+$/` rather than by `check:copy` (whose kiosk-client block
   * bans the em dash alone).
   */
  short_code_shape:
    'The short code must be two to five capital letters, A to Z. Nothing was saved.',
  /**
   * ⛔ IT SAYS WHY, because "not allowed" invites her to try `WARDS` next. The
   * reference is printed on the invoice the home receives, and a family reading
   * a line that says HIV or WARD is the harm the denylist exists to prevent.
   */
  short_code_clinical:
    "That short code reads as a clinical, resident, room or ward word, and it would be printed "
    + "on the home's invoice. Give this account different letters. Nothing was saved.",
  /**
   * ⛔ NAMES ECM RATHER THAN SAYING "RESERVED": she can only avoid a reserved
   * word she can see, and this is the one the machine-minted references use
   * (DD4), so a `ECM 01` would be indistinguishable from an `ECM-7GHK-Q3PY`.
   */
  short_code_reserved:
    'ECM is kept for the references this system makes itself. Give this account different '
    + 'letters. Nothing was saved.',
};

/** Read the refusal code off an untrusted response body (never rendered raw). */
function registryErrorCodeOf(body: unknown): RegistryErrorCode | null {
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  const code = (body as { code: unknown }).code;
  return isRegistryErrorCode(code) ? code : null;
}

/** What the account form collects. `billToId: null` means THIS ACCOUNT PAYS FOR
 *  ITSELF, which is a real answer and not a missing one - no billing party is
 *  invented to stand in for it.
 *
 * ⛔ THE BILLING EMAIL IS DELIBERATELY NOT A MEMBER (T1214). AccountList spreads
 * this whole object into the update payload, and the route's reader IGNORES
 * UNKNOWN KEYS SILENTLY - so a `billingEmail` here would be posted, dropped, and
 * report success. It is also not a column on `accounts` in every case: it lives
 * on whichever row resolveBillToForAccount names, which may be the PARENT that
 * pays. It is its own piece of form state, posted only by setBillingEmail below.
 */
export type AccountInput = {
  name: string;
  addressLine: string | null;
  town: string | null;
  county: string | null;
  eircode: string | null;
  /** A reporting label. Never a join key, and never the bill-to. */
  groupName: string | null;
  /** v35 - one of the four organisation kinds, or null for NEVER CLASSIFIED,
   *  which is a different answer from 'other'. ⛔ Never compared to a service
   *  id: a school may book the nursing service, a home may book a day tour. */
  kind: string | null;
  /** v35 - the organisation's number as typed. Never normalised, never a key. */
  phone: string | null;
  /** v35 - who to ask for at the organisation. ⚠️ Personal data. */
  contactName: string | null;
  /** v35 - the customer's VAT number. ⛔ Stored, never rendered on an invoice:
   *  this operator is not VAT-registered and passenger transport is exempt. */
  vatNumber: string | null;
  billToId: number | null;
  /**
   * ⛔⛔ v40 (T1719 M5) — THE ONE MEMBER WHOSE **ABSENCE** MEANS DIFFERENT THINGS
   * ON THE TWO ROUTES, AND THE SCREEN DECIDES WHICH IT IS SENDING.
   *
   * The route's contract (pinned in test/server/account-write-routes.spec.ts):
   * the key ABSENT means "derive a default from the name" on CREATE and "leave
   * the stored code alone" on UPDATE; an explicit `null` or a blank string means
   * NO CODE on both. So it is OPTIONAL here — `JSON.stringify` drops an
   * `undefined` value, which is how the Add form asks for a derived code — and
   * `AccountList`'s `onSave` builds it PER ROUTE rather than spreading the form
   * state straight through. A `null` posted from an Add form would suppress the
   * derivation for every new account, silently, because a code-less account
   * looks exactly like an ordinary one.
   */
  shortCode?: string | null;
};

/**
 * ⭐ `text?` ADDED AT T1719 M5, AND IT IS THE ONLY WAY A SUCCESS SENTENCE CAN
 * CARRY SOMETHING THE ACTION DISCOVERED. `AccountList`'s `run` takes its `okText`
 * BEFORE the action runs, so a value that only exists in the answer — the short
 * code the server derived — cannot reach the notice any other way. `run` shows
 * `result.text ?? okText`, so every existing caller is unaffected.
 */
export type AccountResult =
  | { ok: true; text?: string }
  /**
   * ⭐ `code?` ADDED AT T1719 M6 (review batch I), AND IT CARRIES THE **CODE**
   * RATHER THAN A CLASSIFICATION OF THE SENTENCE. `AccountList` has to mark the
   * Short code input `aria-invalid` when a save is refused BECAUSE OF THAT BOX,
   * and the only sound discriminator is the machine-readable `code` the server
   * already sends and `registryErrorCodeOf` already narrows. Matching the
   * rendered sentence would couple an accessibility affordance to copy.
   * ⚠️ OPTIONAL, so the failure paths that never see a coded body (the billing
   * email reads, and every non-API failure) are unchanged and say nothing.
   */
  | { ok: false; message: string; code?: RegistryErrorCode | null };

/**
 * ⛔ createAccount's OWN result. `AccountResult` ABOVE gained one OPTIONAL member
 * at T1719 M5 (`text?`) and nothing else.
 *
 * ⚠️ THIS SENTENCE USED TO READ "AND `AccountResult` ABOVE STAYS UNTOUCHED", and
 * the reason it gave still holds for the part it was about: that type is shared
 * by updateAccount and setAccountActive, both of which return a bare
 * `{ ok: true }`; a REQUIRED accountId would break them, and an OPTIONAL one
 * would hand the add path `number | undefined` for no gain. An optional `text?`
 * bars none of that — but a comment that contradicts the code must not survive
 * the edit, so it says what changed instead. This one stays structurally
 * assignable to AccountResult, so AccountList's `run` accepts it unchanged.
 *
 * ⚠️ `accountId` IS NULLABLE, WHICH IS ONE STEP MORE CAUTIOUS THAN THE ROUTE
 * NEEDS AND IS DELIBERATE. POST /api/accounts does return the id today, but the
 * transport casts the body unchecked, so typing it `number` would let a body
 * without one flow on as `undefined` and post `account:undefined` as the
 * compare-and-set token. Null instead makes the compiler force the caller to say
 * what happens when there is no id - which is the same "no key, so BLOCK the
 * billing-email leg" rule the read-failure path already needs.
 */
export type CreateAccountResult =
  | {
    ok: true;
    accountId: number | null;
    /**
     * ⛔ THREE STATES, AND THE THIRD IS **UNKNOWN**, NEVER "NO CODE". A string is
     * the code the server stored; `null` is a server saying it could make none;
     * ABSENT is a server that predates M5 and said nothing at all. Reading the
     * missing key as `null` would tell Helen an account has no code when nobody
     * asked — so the add notice falls back to its plain sentence instead.
     */
    shortCode?: string | null;
  }
  /** ⭐ `code?` mirrors `AccountResult` above (T1719 M6, review batch I): the
   *  Add path refuses a short code as readily as the Edit path does, so the
   *  screen must be able to mark the box on BOTH. */
  | { ok: false; message: string; code?: RegistryErrorCode | null };

/**
 * ⛔ RETURNS THE CODE BESIDE THE SENTENCE SINCE T1719 M6 (review batch I). It
 * used to return a bare string, so the narrowing it already does was thrown away
 * one line later and the screen had nothing but prose to key off. The SENTENCE
 * is still the client's (SEC-5); the CODE never reaches a rendered string.
 */
function accountFailure(e: unknown, verb: string): { message: string; code: RegistryErrorCode | null } {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    /**
     * ⛔⛔ THE CODE NARROWS HERE TOO, AND ITS ABSENCE WAS A REAL DEFECT (T1719
     * M3 output gate, D2). This arm keyed on the STATUS alone, so the server's
     * two short-code refusals - another account holds the code, and DD4's lock -
     * were both replaced by the duplicate-NAME sentence below. The server orders
     * its 409 branches specifically so a code collision is not reported as a
     * name collision; keying on status alone defeated that one layer up.
     * ⚠️ AND IT COMPOUNDED WITH THE SCREEN: AccountList runs a client-side
     * duplicate-NAME pre-check immediately before this POST, so the operator was
     * told the name clashed one keystroke after the list proved it did not.
     * ⛔ SAME SHAPE AS THE 400 ARM BELOW, deliberately: the code only ever
     * NARROWS, and a codeless 409 keeps the sentence that was always here.
     */
    const code = e instanceof ApiError ? registryErrorCodeOf(e.body) : null;
    if (code !== null) return { message: REGISTRY_ERROR_COPY[code], code };
    return {
      message: 'There is already an account with that name. Check the list, or use a name that tells them apart.',
      code: null,
    };
  }
  if (status === 404) {
    return { message: 'That account is no longer on the list. Reload and try again.', code: null };
  }
  if (status === 400) {
    // ⛔ THE CODE ONLY EVER NARROWS — see the sibling note in
    // destination-actions.ts. A codeless 400 (missing name, missing reason) must
    // keep the sentence that was always here.
    const code = e instanceof ApiError ? registryErrorCodeOf(e.body) : null;
    if (code !== null) return { message: REGISTRY_ERROR_COPY[code], code };
    return {
      message: 'Check the details. An account needs a name, and each line has to be short enough to fit on a booking.',
      code: null,
    };
  }
  return { message: `Could not ${verb}. Try again in a moment.`, code: null };
}

/**
 * ⭐ EVERY WRITE BELOW CARRIES `note` SINCE T651 M4, AND IT IS REQUIRED.
 *
 * It is a separate argument rather than a field on AccountInput because it is
 * not a column on `accounts`: it goes to the v27 lifecycle journal, beside who
 * acted and when, inside the same transaction as the change. Putting it on the
 * input type would have made it look like something the account stores.
 * ⚠️ The 400 sentence a missing note produces is deliberately the SAME one as
 * any other bad field (see accountFailure) - the client refuses first, with its
 * own wording, so the operator never has to read the route's sentence.
 */
export async function createAccount(input: AccountInput, note: string): Promise<CreateAccountResult> {
  try {
    // The route has always answered `{ ok: true, accountId }`; until T1214 this
    // client threw the id away, which is why the add path had nothing to attach
    // a billing email to. Read defensively rather than cast: see the type above.
    const body = await postJson<Record<string, unknown>>('/api/accounts', { ...input, note });
    const raw = body['accountId'];
    const accountId = typeof raw === 'number' && Number.isInteger(raw) && raw > 0 ? raw : null;
    // ⛔ THE SAME DEFENSIVE READ, FOR THE SAME REASON — `postJson` casts the body
    // unchecked. ⚠️ AND THE THREE-WAY SPLIT IS THE POINT: anything that is not a
    // string and not an explicit `null` (a key that is absent, or a body from a
    // server that predates M5) is UNKNOWN, and the caller must not render it as
    // "no code could be made".
    const rawCode = body['shortCode'];
    const shortCode = typeof rawCode === 'string' ? rawCode : rawCode === null ? null : undefined;
    return { ok: true, accountId, shortCode };
  } catch (e) {
    return { ok: false, ...accountFailure(e, 'add that account') };
  }
}

export async function updateAccount(
  accountId: number,
  input: AccountInput,
  note: string,
): Promise<AccountResult> {
  try {
    await postJson('/api/accounts/update', { accountId, ...input, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, ...accountFailure(e, 'save that account') };
  }
}

/** R38 - deactivate by flag, never delete. Hidden from new work; every booking
 *  already taken keeps its account and its price. */
export async function setAccountActive(
  accountId: number,
  active: boolean,
  note: string,
): Promise<AccountResult> {
  try {
    await postJson('/api/accounts/active', { accountId, active, note });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      ...accountFailure(e, active ? 'put that account back in use' : 'take that account out of use'),
    };
  }
}

// ---------------------------------------------------------------------------
// T1214 - THE BILLING EMAIL. ONE READ, ONE WRITE, AND THEY RESOLVE THE SAME ROW.
//
// ⭐ THE FIELD IS NOT ALWAYS ON THE ACCOUNT, WHICH IS THE WHOLE REASON THESE TWO
// ARE A PAIR RATHER THAN TWO MORE FIELDS ON THE FORM. `billing_email` exists on
// `accounts` AND on `billing_parties`, and the server writes whichever row
// resolveBillToForAccount names. Both calls below go through that one resolver,
// so the box can never show one row while the save lands on another. `target`
// is what lets the screen SAY which of the two it is showing, instead of leaving
// one ambiguous box to stand for both.
// ---------------------------------------------------------------------------

/** Which identity the address the screen is showing belongs to. `billing-party`
 *  means a PARENT that may pay for several sites; `account` means this one. */
export type BillingEmailTarget = 'account' | 'billing-party';

export type BillingEmailRead =
  | {
    ok: true;
    target: BillingEmailTarget;
    /**
     * ⛔ THE COMPARE-AND-SET TOKEN, AND IT IS NOT DECORATION. It names the payer
     * this address was read from. The write takes it back as `expectRollUpKey`
     * and refuses if the payer has moved since - so dropping it here would not
     * fail, it would silently DISARM that refusal and let a site's address land
     * on a shared parent.
     */
    rollUpKey: string;
    billingEmail: string | null;
    /**
     * ⭐ board 3059 - THE PAYER'S RESIDENT-NAMES SWITCH, read off the same payer as
     * the address. ⛔ `true` ONLY FOR A LITERAL `true`: a server that predates the
     * field omits it, and the safe reading of "not said" is OFF.
     */
    residentNamesOnInvoices: boolean;
  }
  | { ok: false; message: string };

/**
 * GET /api/billing-email. Fired once per Edit by the account book, never on Add
 * and never as part of the list load: the account list route deliberately does
 * not carry this field, because none of its other three consumers has any use
 * for who a customer's invoices go to.
 */
export async function getBillingEmail(accountId: number): Promise<BillingEmailRead> {
  try {
    const body = await getJson<Record<string, unknown>>(
      `/api/billing-email?accountId=${String(accountId)}`,
    );
    const target = body['target'];
    const rollUpKey = body['rollUpKey'];
    // ⛔ NARROWED RATHER THAN CAST. `target` picks which sentence the screen
    // shows ("this account" vs "the parent that pays"), and a missing rollUpKey
    // must read as "no key" so the caller BLOCKS the write rather than posting
    // it unguarded - the transport casts bodies unchecked, so nothing else here
    // would notice a shape that drifted.
    if ((target !== 'account' && target !== 'billing-party') || typeof rollUpKey !== 'string') {
      return { ok: false, message: 'Could not read the billing email. Try opening the account again.' };
    }
    const billingEmail = body['billingEmail'];
    return {
      ok: true,
      target,
      rollUpKey,
      billingEmail: typeof billingEmail === 'string' ? billingEmail : null,
      residentNamesOnInvoices: body['residentNamesOnInvoices'] === true,
    };
  } catch {
    return { ok: false, message: 'Could not read the billing email. Try opening the account again.' };
  }
}

/**
 * ⛔ ITS OWN FAILURE MAPPER, AND REUSING accountFailure WOULD BE A LIE ON EVERY
 * ARM THAT MATTERS. That mapper turns any 409 into "there is already an account
 * with that name" - here a 409 means the PAYER MOVED, which is a different
 * problem with a different fix - and its 404 arm names the account list rather
 * than the payer.
 *
 * ⭐ EVERY SENTENCE LEADS WITH WHAT DID LAND. This call is always the SECOND leg
 * of a save: the account create or update has already committed and there is no
 * rollback, so a bare "could not save" would send the operator back to re-enter
 * details that are already stored. `saved` is that clause, passed by the caller
 * because only it knows whether the account was added or edited.
 *
 * ⚠️ NO EM DASH IN ANY OF THIS COPY. The server's own 409 sentence carries one;
 * it can, because check:copy scans src/client and src/shared and not src/server.
 */
function billingEmailFailure(e: unknown, saved: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    return `${saved}, but the billing email was not applied: it now bills to a different payer. `
      + 'Open the account again and set the address against the payer shown.';
  }
  if (status === 404) {
    return `${saved}, but the billing email was not applied. That account is no longer on the list. `
      + 'Reload and try again.';
  }
  if (status === 400) {
    return `${saved}, but the billing email was not applied. It has to be a full email address.`;
  }
  return `${saved}, but the billing email was not applied. Try again in a moment.`;
}

/**
 * POST /api/billing-email. `null` CLEARS the address and is a real operation:
 * one that has stopped working must be removable without inventing a
 * placeholder.
 *
 * `expectRollUpKey` is the key getBillingEmail returned when this account was
 * opened. Sending it is what makes the server refuse rather than write when the
 * payer moved underneath the form; a caller that omits it gets the old
 * unguarded behaviour, so it is REQUIRED here even though the route treats it as
 * optional. Never a key the screen invented - the one exception is the add path,
 * where the account has just been created and `account:<newId>` is the key the
 * resolver is known to answer for an account that bills itself.
 */
export async function setBillingEmail(
  accountId: number,
  billingEmail: string | null,
  note: string,
  expectRollUpKey: string,
  saved: string,
): Promise<AccountResult> {
  try {
    await postJson('/api/billing-email', { accountId, billingEmail, expectRollUpKey, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: billingEmailFailure(e, saved) };
  }
}

// ---------------------------------------------------------------------------
// ⭐ board 3059 - THE PAYER'S RESIDENT-NAMES SWITCH. One write, keyed on the
// payer the form read (`rollUpKey`), with the form's Why as its reason.
// ---------------------------------------------------------------------------

/**
 * Its own failure mapper, for `billingEmailFailure`'s reason: a 409 here means
 * the PAYER MOVED (code `payer_moved`), never a name clash. ⛔ Every sentence
 * says the setting did not change, because a refused write changed nothing.
 */
function residentNamesFailure(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    return 'The setting was not changed: this account now bills to a different payer. '
      + 'Open the account again and set it against the payer shown.';
  }
  if (status === 404) return 'The setting was not changed. That account is no longer on the list. Reload and try again.';
  if (status === 400) return 'The setting was not changed. Say why in the Why box, then try again.';
  return 'The setting was not changed. Try again in a moment.';
}

/**
 * POST /api/invoice-resident-names. `on` is the new setting; `expectRollUpKey`
 * is the key getBillingEmail returned when the account was opened (the route
 * refuses without it, and refuses if the account now bills to someone else);
 * `note` is the form's Why, filed with the change.
 */
export async function setInvoiceResidentNames(
  accountId: number,
  on: boolean,
  expectRollUpKey: string,
  note: string,
): Promise<AccountResult> {
  try {
    await postJson('/api/invoice-resident-names', { accountId, on, expectRollUpKey, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: residentNamesFailure(e) };
  }
}

// ---------------------------------------------------------------------------
// Board 2764 M1 - THE BILLING PARTY'S POSTAL ADDRESS. The email pair's shape:
// one read, one write, both resolved server-side through the one resolver.
// The screen shows it only when the account bills to a party; an account that
// bills itself is addressed with its own address fields, and the route refuses
// to write that arm.
// ---------------------------------------------------------------------------

/** The four lines of a billing party's postal address. */
export type BillingAddress = {
  addressLine: string | null;
  town: string | null;
  county: string | null;
  eircode: string | null;
};

export type BillingAddressRead =
  | {
    ok: true;
    target: BillingEmailTarget;
    /** The compare-and-set token, as for the billing email. The write REQUIRES it. */
    rollUpKey: string;
    address: BillingAddress;
  }
  | { ok: false; message: string };

const ADDRESS_READ_FAILED = 'Could not read the billing address. Try opening the account again.';

const textOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** GET /api/billing-address. Fired once per Edit, only for a party payer. */
export async function getBillingAddress(accountId: number): Promise<BillingAddressRead> {
  try {
    const body = await getJson<Record<string, unknown>>(
      `/api/billing-address?accountId=${String(accountId)}`,
    );
    const target = body['target'];
    const rollUpKey = body['rollUpKey'];
    const address = body['address'];
    // ⛔ NARROWED RATHER THAN CAST, as getBillingEmail is: a missing key must read
    // as "no key" so the screen blocks the write instead of posting it unguarded.
    if ((target !== 'account' && target !== 'billing-party') || typeof rollUpKey !== 'string'
      || typeof address !== 'object' || address === null) {
      return { ok: false, message: ADDRESS_READ_FAILED };
    }
    const a = address as Record<string, unknown>;
    return {
      ok: true,
      target,
      rollUpKey,
      address: {
        addressLine: textOrNull(a['addressLine']),
        town: textOrNull(a['town']),
        county: textOrNull(a['county']),
        eircode: textOrNull(a['eircode']),
      },
    };
  } catch {
    return { ok: false, message: ADDRESS_READ_FAILED };
  }
}

/** Its own mapper, for the reason billingEmailFailure states: every sentence
 *  leads with what DID land, and a 409 here means the payer moved. */
function billingAddressFailure(e: unknown, saved: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    return `${saved}, but the billing address was not applied: it now bills to a different payer. `
      + 'Open the account again and set the address against the payer shown.';
  }
  if (status === 404) {
    return `${saved}, but the billing address was not applied. That account is no longer on the list. `
      + 'Reload and try again.';
  }
  if (status === 400) {
    return `${saved}, but the billing address was not applied. Each line has to be short enough to fit `
      + 'on an invoice.';
  }
  return `${saved}, but the billing address was not applied. Try again in a moment.`;
}

/**
 * POST /api/billing-address. A blank line clears that line. `expectRollUpKey`
 * is the key the screen read when the account was opened, never one it made up;
 * the route refuses without it.
 */
export async function setBillingAddress(
  accountId: number,
  address: BillingAddress,
  note: string,
  expectRollUpKey: string,
  saved: string,
): Promise<AccountResult> {
  try {
    await postJson('/api/billing-address', { accountId, ...address, expectRollUpKey, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: billingAddressFailure(e, saved) };
  }
}

// ---------------------------------------------------------------------------
// Board 2764 M2 (R-A3, R-11) - "SET UP BANK DETAILS". Creates or reuses the
// payer's record at Stripe and answers the bank account that payer transfers
// to. Nothing is invoiced. The server answers exactly four fields and never the
// Stripe customer id or the email.
// ---------------------------------------------------------------------------

/** What the press answered: Stripe's own holder name, the IBAN and the BIC. */
export type BankDetails = {
  accountHolderName: string | null;
  iban: string;
  bic: string | null;
  /** TRUE when this press created the payer's record at Stripe. */
  created: boolean;
};

export type BankDetailsResult = { ok: true; details: BankDetails } | { ok: false; message: string };

const NO_BANK_DETAILS = 'Stripe did not give any bank details for this payer. Nothing was set up. Try again later.';

/**
 * Its own mapper: a 409 here is either the payer moving or a missing billing
 * email, and the CODE says which (the body is read to KEY the copy, never shown).
 * Every sentence says what to do next.
 */
function bankDetailsFailure(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  const body = e instanceof ApiError && typeof e.body === 'object' && e.body !== null
    ? (e.body as Record<string, unknown>)
    : {};
  const code = body['code'];
  if (code === 'no_billing_email') {
    return 'This payer has no billing email yet. Add one and save the account, then set up bank details.';
  }
  if (code === 'payer_changed' || status === 404) {
    return 'This account now bills to a different payer, so nothing was set up. Open the account again and try again.';
  }
  if (code === 'bank_details_unavailable') return NO_BANK_DETAILS;
  if (status === 503) return 'Bank details cannot be set up on this system.';
  if (status === 400) return 'The request was refused. Open the account again and try again.';
  return 'Stripe could not be reached, so nothing was set up. Try again in a moment.';
}

/**
 * POST /api/billing-bank-details. `expectRollUpKey` is the key getBillingEmail
 * returned when the account was opened; the route refuses without it, and
 * refuses if the account now bills to someone else. `note` is the form's reason.
 */
export async function setUpBankDetails(
  accountId: number,
  expectRollUpKey: string,
  note: string,
): Promise<BankDetailsResult> {
  let body: Record<string, unknown>;
  try {
    body = await postJson<Record<string, unknown>>('/api/billing-bank-details', { accountId, expectRollUpKey, note });
  } catch (e) {
    return { ok: false, message: bankDetailsFailure(e) };
  }
  // ⛔ NARROWED RATHER THAN CAST: an IBAN that is not text is no IBAN, and the
  // screen must say so rather than print `undefined` beside the word.
  const iban = body['iban'];
  if (typeof iban !== 'string' || iban === '') return { ok: false, message: NO_BANK_DETAILS };
  return {
    ok: true,
    details: {
      accountHolderName: textOrNull(body['accountHolderName']),
      iban,
      bic: textOrNull(body['bic']),
      created: body['created'] === true,
    },
  };
}
